/**
 * AssignJobsDialog — Phase 86.I Pass B: "give money jobs".
 *
 * Splits ONE account's balance across bills / safety / goal / flexible. Planning
 * only: nothing moves, no balance changes. The preview is computed by the same
 * backend model that a save would produce, so what you see is what you get.
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/lib/supabaseClient';
import { formatMoney } from '@/lib/format';

const API = process.env.REACT_APP_BACKEND_URL;

const PURPOSE_OPTIONS = [
  { value: 'bills', label: 'Everyday & bills' },
  { value: 'safety', label: 'Safety reserve' },
  { value: 'goal', label: 'A savings goal' },
  { value: 'flexible', label: 'Keep flexible' },
];
const METHOD_OPTIONS = [
  { value: 'amount', label: 'Amount' },
  { value: 'percent', label: '% of balance' },
  { value: 'remainder', label: 'Whatever is left' },
];

const auth = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
};

function rowsFromAccount(account) {
  return (account.purposes || [])
    .filter((p) => p.method !== 'account_purpose')
    .map((p, i) => ({
      key: `${i}`, purpose: p.purpose, method: p.method || 'amount',
      value: p.method === 'percent' ? '' : String(p.amount ?? ''),
      goal_id: p.goal_id || '',
    }));
}

function Figure({ label, before, after, currency, testid }) {
  const changed = after != null && before != null && Math.abs(after - before) > 0.005;
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm" data-testid={testid}>
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">
        <span className={changed ? 'text-muted-foreground line-through mr-1.5' : ''}>
          {before == null ? '—' : formatMoney(before, currency)}
        </span>
        {changed && <span className="font-medium text-charcoal">{formatMoney(after, currency)}</span>}
      </span>
    </div>
  );
}

export default function AssignJobsDialog({ account, plan, open, onOpenChange, onSaved }) {
  const [rows, setRows] = useState(() => rowsFromAccount(account));
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const goals = plan.available_goals || [];
  const ccy = account.currency;
  const base = plan.base_currency;

  const assignedFixed = useMemo(() => rows
    .filter((r) => r.method === 'amount')
    .reduce((s, r) => s + (Number(r.value) || 0), 0), [rows]);
  const assignedPct = useMemo(() => rows
    .filter((r) => r.method === 'percent')
    .reduce((s, r) => s + (Number(r.value) || 0), 0), [rows]);
  const overBalance = assignedFixed - account.balance > 0.05;
  const overPercent = assignedPct > 100.05;
  const missingGoal = rows.some((r) => r.purpose === 'goal' && !r.goal_id);
  const invalid = overBalance || overPercent || missingGoal;

  const payload = () => ({
    allocations: rows
      .filter((r) => r.method === 'remainder' || Number(r.value) > 0)
      .map((r) => ({
        purpose: r.purpose,
        method: r.method,
        value: r.method === 'remainder' ? 0 : Number(r.value) || 0,
        goal_id: r.purpose === 'goal' ? r.goal_id : null,
      })),
  });

  const setRow = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const addRow = () => setRows((rs) => [...rs, {
    key: `n${Date.now()}`, purpose: 'safety', method: 'amount', value: '', goal_id: '',
  }]);

  const run = async (mode) => {
    setError(null);
    const set = mode === 'save' ? setSaving : setBusy;
    set(true);
    try {
      const headers = await auth();
      const url = `${API}/api/cash-plan/allocations/${account.id}${mode === 'save' ? '' : '/preview'}`;
      const res = mode === 'save'
        ? await axios.put(url, payload(), { headers, timeout: 60000 })
        : await axios.post(url, payload(), { headers, timeout: 60000 });
      if (mode === 'save') {
        onSaved?.(res.data);
        toast.success('Saved. Nothing moved — this is a plan, not a transfer.');
        onOpenChange(false);
      } else {
        setPreview(res.data);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail.replace(/_/g, ' ') : 'Could not reach the planner. Try again.');
    } finally {
      set(false);
    }
  };

  const cur = plan.summary;
  const nxt = preview?.summary;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="assign-jobs-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">Give {account.name} a job</DialogTitle>
          <DialogDescription>
            {formatMoney(account.balance, ccy)} reported
            {account.as_of ? ` as of ${account.as_of}` : ''}. Split it across the jobs it
            actually does. Planning only — no money moves and no balance changes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {rows.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="assign-jobs-empty">
              This account has no jobs yet. Add one below.
            </p>
          )}
          {rows.map((r, i) => (
            <div key={r.key} className="grid grid-cols-1 sm:grid-cols-[1fr_140px_1fr_auto] gap-2 items-start"
                 data-testid={`assign-row-${i}`}>
              <Select value={r.purpose} onValueChange={(v) => setRow(r.key, { purpose: v, goal_id: '' })}>
                <SelectTrigger data-testid={`assign-purpose-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PURPOSE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={r.method} onValueChange={(v) => setRow(r.key, { method: v })}>
                <SelectTrigger data-testid={`assign-method-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {METHOD_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {r.method === 'remainder' ? (
                <p className="text-xs text-muted-foreground self-center">Takes whatever is unassigned.</p>
              ) : (
                <Input type="number" min="0" inputMode="decimal" value={r.value}
                       placeholder={r.method === 'percent' ? '%' : ccy}
                       onChange={(e) => setRow(r.key, { value: e.target.value })}
                       data-testid={`assign-value-${i}`} />
              )}
              <Button variant="ghost" size="icon" aria-label="Remove"
                      onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}
                      data-testid={`assign-remove-${i}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
              {r.purpose === 'goal' && (
                <div className="sm:col-span-4">
                  <Select value={r.goal_id} onValueChange={(v) => setRow(r.key, { goal_id: v })}>
                    <SelectTrigger data-testid={`assign-goal-${i}`}>
                      <SelectValue placeholder="Which goal?" />
                    </SelectTrigger>
                    <SelectContent>
                      {goals.map((g) => (
                        <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          ))}

          <Button variant="outline" size="sm" onClick={addRow} data-testid="assign-add-row">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add a job
          </Button>

          <div className="text-xs text-muted-foreground" data-testid="assign-jobs-tally">
            Assigned {formatMoney(assignedFixed, ccy)}
            {assignedPct > 0 ? ` + ${assignedPct}%` : ''} of {formatMoney(account.balance, ccy)}.
            {overBalance && <span className="text-destructive"> That is more than this account holds.</span>}
            {overPercent && <span className="text-destructive"> Percentages add up to more than 100%.</span>}
            {missingGoal && <span className="text-destructive"> Pick a goal for the goal allocation.</span>}
          </div>

          <div className="rounded-xl border border-border bg-muted/30 p-4" data-testid="assign-preview">
            <div className="flex items-center justify-between gap-3 mb-2">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">
                Effect on your plan
              </p>
              <Button variant="outline" size="sm" onClick={() => run('preview')}
                      disabled={busy || invalid} data-testid="assign-preview-btn">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Preview'}
              </Button>
            </div>
            {preview ? (
              <div className="space-y-1.5">
                <Figure testid="preview-bills" label="Available for bills" currency={base}
                        before={cur.available_for_bills.display_amount}
                        after={nxt.available_for_bills.display_amount} />
                <Figure testid="preview-safety" label="Safety reserve" currency={base}
                        before={cur.assigned_to_priorities.safety.display_amount}
                        after={nxt.assigned_to_priorities.safety.display_amount} />
                <Figure testid="preview-unassigned" label="Not yet assigned" currency={base}
                        before={plan.allocation_breakdown?.by_purpose?.not_assigned?.display}
                        after={preview.allocation_breakdown?.by_purpose?.not_assigned?.display} />
                <div className="flex items-baseline justify-between gap-3 text-sm pt-1.5 border-t border-border"
                     data-testid="preview-buffer">
                  <span className="text-muted-foreground">Safety buffer</span>
                  <span className="tabular-nums">
                    {nxt.safety_buffer.state === 'ok'
                      ? `${nxt.safety_buffer.months} months of ${nxt.safety_buffer.target_months}`
                      : 'still not calculable'}
                  </span>
                </div>
                {nxt.total_reported.converted && (
                  <p className="text-[11px] text-muted-foreground pt-1">
                    Shown in {base}; this account is held in {ccy}.
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Preview the effect before you save. Nothing is written until you press save.
              </p>
            )}
          </div>

          {error && (
            <p className="text-sm text-destructive" data-testid="assign-jobs-error">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="assign-cancel">Cancel</Button>
          <Button onClick={() => run('save')} disabled={saving || invalid} data-testid="assign-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save these jobs'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
